import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertOctagon, UserCog, X } from 'lucide-react';
import {
  acknowledgeImpersonationTransition,
  checkImpersonationDeadline,
  parseServerInstant,
  useAuthStore,
  useImpersonationRefusal,
  useImpersonationTransition,
} from '../store/authStore';

/** The last five minutes of a session get the strong amber state. */
const WARN_MS = 5 * 60_000;

/** How long a notice stays up if nobody dismisses it. */
const NOTICE_MS = {
  ended: 20_000,
  signedOut: 20_000,
  // A one-liner about one click; it has said its piece by then.
  forbidden: 8_000,
} as const;

/**
 * Whole minutes left while inside the last five, else null. `left` is
 * milliseconds to the deadline (null: not in a session).
 */
function warnMinutes(left: number | null): number | null {
  return left !== null && left > 0 && left <= WARN_MS ? Math.ceil(left / 60_000) : null;
}

/** HH:MM in the viewer's local time. */
function clock(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * `ended` / `signedOut` replace the banner once a session is over (see
 * `impersonation.notice.*`); `forbidden` is the one-line notice inside the
 * banner while it runs: a request was refused by the session policy.
 */
type Notice = keyof typeof NOTICE_MS;

/**
 * Everything the SPA shows and does about an admin's login-as session
 * ("Log ind som") that is not a page: the amber banner above the header
 * while one runs, the clock that ends it, and — for every way a session
 * can begin or end — the things that must follow the swap of identity.
 *
 * Mounted once, in App, above the header, and never unmounted, because
 * it is what reacts when the auth store starts or ends a session — from
 * the dialog, the Afslut button, the clock below, or the api client's
 * 401 handler, none of which has a router or a query client of its own.
 *
 * The banner says, while a session runs: whom the admin is logged in as
 * and in which role; what that means — "everything you do is recorded"
 * for a full session, "view only — changes are disabled" for a debtor or
 * operator target (the server refuses every write there); when it ends
 * (and, in its last five minutes, in strong amber, how long is left);
 * and, in a notice slot under the main line, that something the admin
 * tried is not possible in a session (a 403 `impersonation_forbidden`
 * from any request — the page that made it still shows its own error).
 *
 * On every transition (`useImpersonationTransition`):
 *
 *  1. Cancel what is in flight and empty the query cache, THEN
 *     navigate. A warm cache read by the next page is how one user's
 *     data ends up in another's view (Lendora once showed one user's
 *     bank details in the next view that way), and a request still on
 *     its way would land in the new identity's cache. The cache is also
 *     emptied the moment the identity flips (first effect), and App
 *     renders no routes until this is done (IdentityGate), so no page
 *     ever reads the cache — or asks the API — as the new identity at
 *     the old identity's address.
 *  2. Go where the transition says, with the router state it carries:
 *     the target's landing page after a start, the page the session was
 *     started from after an exit, /login (`state.from` = that same page,
 *     so signing in lands back on it) when there is no usable admin
 *     login to return to. Through the router — never a hard redirect,
 *     which would lose where the admin was.
 *  3. After an automatic end (time ran out, the server ended it from
 *     somewhere else, the admin signed out in another tab) say so in one
 *     line: the admin may have been in the middle of something.
 *
 * It renders nothing while there is nothing to show.
 */
export function ImpersonationBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const impersonation = useAuthStore((s) => s.impersonation);
  const user = useAuthStore((s) => s.user);
  const transition = useImpersonationTransition((s) => s.transition);
  const refusals = useImpersonationRefusal((s) => s.seq);
  const [notice, setNotice] = useState<Notice | null>(null);
  // Whole minutes left while inside the last five; null before that.
  // Seeded from the store, so a session reloaded inside its last five
  // minutes is strong from the first paint, not one frame later.
  const [minutesLeft, setMinutesLeft] = useState<number | null>(() => {
    const deadline = useAuthStore.getState().impersonation?.deadline;
    return deadline === undefined ? null : warnMinutes(deadline - Date.now());
  });
  const handledSeq = useRef(0);
  const seenRefusals = useRef(refusals);
  // The transition whose navigation has been issued but not yet seen.
  const awaiting = useRef<{ seq: number; fromKey: string; fallback: number } | null>(null);
  const locationKey = useRef(location.key);
  locationKey.current = location.key;

  const release = (seq: number) => {
    const waiting = awaiting.current;
    if (waiting && waiting.seq === seq) {
      window.clearTimeout(waiting.fallback);
      awaiting.current = null;
    }
    acknowledgeImpersonationTransition(seq);
  };

  const sessionId = impersonation?.id ?? null;

  // The identity behind every cached answer just changed: cancel and
  // empty the cache in the same tick as the swap, before React renders
  // anything under the new identity.
  useEffect(
    () =>
      useAuthStore.subscribe((state, previous) => {
        if ((state.impersonation?.id ?? null) === (previous.impersonation?.id ?? null)) {
          return;
        }
        void queryClient.cancelQueries();
        queryClient.clear();
      }),
    [queryClient],
  );

  // The clock. The session ends at its `deadline` (browser clock,
  // computed at the start from the length the server stated) whatever
  // else happens; the server refuses the token from then on as well, and
  // a 401 ends it locally too — this is what makes the end visible the
  // moment it happens instead of on the next request. Checked every
  // second, and at once whenever the tab wakes (`visibilitychange`,
  // `focus`, `pageshow`): timers in a hidden tab or a sleeping laptop run
  // late, so no single long timeout is trusted with it. The same check
  // says how long is left, for the last-five-minutes state.
  useEffect(() => {
    if (sessionId === null) {
      setMinutesLeft(null);
      return;
    }
    const tick = () => {
      setMinutesLeft(warnMinutes(checkImpersonationDeadline()));
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('focus', tick);
    window.addEventListener('pageshow', tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('focus', tick);
      window.removeEventListener('pageshow', tick);
    };
  }, [sessionId]);

  // After a start or an exit: cache, then navigation, then the notice.
  useEffect(() => {
    if (!transition || transition.seq <= handledSeq.current) return;
    handledSeq.current = transition.seq;
    const { seq, kind, to, state, signedOut } = transition;
    void (async () => {
      try {
        await queryClient.cancelQueries();
        queryClient.clear();
        setNotice(
          kind === 'started' ? null : signedOut ? 'signedOut' : kind === 'ended' ? 'ended' : null,
        );
        // App holds its routes back until this transition is acknowledged.
        // Release them once the router has actually moved — not when
        // `navigate` returns: its location update lands after the store's,
        // and routes rendered at the old address under the new identity are
        // exactly what the hold-back is for. The timer is the way out if
        // the router never reports a move.
        awaiting.current = {
          seq,
          fromKey: locationKey.current,
          fallback: window.setTimeout(() => release(seq), 2000),
        };
        navigate(to, { replace: true, ...(state ? { state } : {}) });
      } catch {
        release(seq);
      }
    })();
    // (`release` only touches refs and the store: no need to depend on it.)
  }, [transition, queryClient, navigate]);

  // The router has moved since the last transition's navigation was issued.
  useEffect(() => {
    const waiting = awaiting.current;
    if (waiting && location.key !== waiting.fromKey) release(waiting.seq);
  }, [location.key]);

  // A request inside the session was refused by the session policy.
  // (Only while one is running: a refusal is about the session.)
  useEffect(() => {
    if (refusals === seenRefusals.current) return;
    seenRefusals.current = refusals;
    if (useAuthStore.getState().impersonation) setNotice('forbidden');
  }, [refusals]);

  // The in-session notice belongs to its session: gone with it.
  useEffect(() => {
    if (sessionId === null) setNotice((n) => (n === 'forbidden' ? null : n));
  }, [sessionId]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), NOTICE_MS[notice]);
    return () => window.clearTimeout(timer);
    // A second refusal re-arms the timer: the line was just said again.
  }, [notice, refusals]);

  if (impersonation && user) {
    const name = user.name?.trim() || user.email;
    const role =
      user.role === 'operator'
        ? t('auth.roleOperator')
        : user.role === 'debtor'
          ? t('auth.roleDebtor')
          : t('auth.roleLenderHuman');
    const endsAt = parseServerInstant(impersonation.expires_at);
    // Strong amber for the last five minutes: the admin may be mid-task
    // and the session is about to stop answering.
    const expiring = minutesLeft !== null;
    const expiry = expiring
      ? t('impersonation.banner.expiresSoon', { minutes: minutesLeft })
      : Number.isFinite(endsAt)
        ? t('impersonation.banner.expires', { time: clock(endsAt) })
        : null;
    const inkSoft = expiring ? 'text-[var(--color-ink)]' : 'text-[var(--color-ink-soft)]';

    return (
      <div
        role="region"
        aria-label={t('impersonation.banner.region')}
        data-view-only={impersonation.view_only ? 'true' : undefined}
        data-expiring={expiring ? 'true' : undefined}
        // Sticky, so it is on screen however far the page is scrolled:
        // an admin must never lose sight of whom they are acting as.
        className={`sticky top-0 z-50 border-b ${
          expiring
            ? 'border-[var(--color-ink)] bg-[var(--color-alert)]'
            : 'border-[var(--color-alert)] bg-[var(--color-alert-soft)]'
        }`}
      >
        <div className="mx-auto max-w-[1400px] px-[var(--page-gutter)] py-2.5 flex items-center gap-3">
          <UserCog
            className={`w-4 h-4 shrink-0 ${expiring ? 'text-[var(--color-ink)]' : 'text-[var(--color-alert)]'}`}
            strokeWidth={1.5}
            aria-hidden
          />
          <div className="min-w-0 flex-1 flex flex-wrap items-baseline gap-x-4 gap-y-0.5">
            <div className="min-w-0 flex flex-wrap items-baseline gap-x-2">
              <span className={`font-mono text-[11px] uppercase tracking-[0.14em] ${inkSoft}`}>
                {t('impersonation.banner.label')}
              </span>
              <span
                className="font-mono text-sm font-medium text-[var(--color-ink)] break-all"
                title={user.email}
              >
                {name}
              </span>
              <span className={`font-mono text-[11px] uppercase tracking-[0.14em] ${inkSoft}`}>
                · {role}
              </span>
            </div>
            <span
              className={`text-xs ${
                impersonation.view_only || expiring
                  ? 'text-[var(--color-ink)] font-medium'
                  : 'text-[var(--color-ink-soft)]'
              }`}
            >
              {impersonation.view_only
                ? t('impersonation.banner.viewOnly')
                : t('impersonation.banner.recorded')}
              {expiry && ` · ${expiry}`}
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              void useAuthStore.getState().endImpersonation();
            }}
            className={`shrink-0 min-h-9 px-3 py-1.5 text-xs font-mono uppercase tracking-wider border border-[var(--color-ink)] transition-colors ${
              expiring
                ? 'bg-[var(--color-ink)] text-[var(--color-paper)] hover:bg-transparent hover:text-[var(--color-ink)]'
                : 'text-[var(--color-ink)] hover:bg-[var(--color-ink)] hover:text-[var(--color-paper)]'
            }`}
          >
            {t('impersonation.end')}
          </button>
        </div>
        {notice === 'forbidden' && (
          // The notice slot: one line under the main one, for what the
          // session policy just refused. Dismissable, and gone by itself.
          <div
            role="status"
            className="mx-auto max-w-[1400px] px-[var(--page-gutter)] pb-2 flex items-center gap-3"
          >
            <AlertOctagon
              className={`w-4 h-4 shrink-0 ${expiring ? 'text-[var(--color-ink)]' : 'text-[var(--color-alert)]'}`}
              strokeWidth={1.5}
              aria-hidden
            />
            <p className="flex-1 min-w-0 text-xs text-[var(--color-ink)]">
              {t('impersonation.notice.forbidden')}
            </p>
            <button
              type="button"
              onClick={() => setNotice(null)}
              aria-label={t('impersonation.notice.dismiss')}
              className="shrink-0 inline-flex h-8 w-8 items-center justify-center text-[var(--color-ink-soft)] hover:text-[var(--color-ink)]"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        )}
      </div>
    );
  }

  if (notice === 'ended' || notice === 'signedOut') {
    return (
      <div
        role="status"
        className="sticky top-0 z-50 border-b border-[var(--color-alert)] bg-[var(--color-alert-soft)]"
      >
        <div className="mx-auto max-w-[1400px] px-[var(--page-gutter)] py-2 flex items-center gap-3">
          <AlertOctagon
            className="w-4 h-4 shrink-0 text-[var(--color-alert)]"
            strokeWidth={1.5}
            aria-hidden
          />
          <p className="flex-1 text-xs text-[var(--color-ink)]">
            {t(`impersonation.notice.${notice}`)}
          </p>
          <button
            type="button"
            onClick={() => setNotice(null)}
            aria-label={t('impersonation.notice.dismiss')}
            className="shrink-0 inline-flex h-8 w-8 items-center justify-center text-[var(--color-ink-soft)] hover:text-[var(--color-ink)]"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
